import React from 'react';
import {
  Box, Container, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { C, Section, SectionLabel } from './theme';

const cols = [
  { key: 'sm', label: 'Self-Managed VPS', highlight: true },
  { key: 'mv', label: 'Managed VPS', highlight: false },
  { key: 'sh', label: 'Shared Hosting', highlight: false },
  { key: 'ds', label: 'Dedicated Server', highlight: false },
];

const rows = [
  {
    label: 'Description',
    sm: 'Your own virtual private server with full root access',
    mv: 'Start with expert support',
    sh: 'Shared web hosting on pooled resources',
    ds: 'Single-tenant physical server isolation',
  },
  {
    label: 'Control level',
    sm: 'Complete control to install custom software',
    mv: 'High control; some actions abstracted',
    sh: 'Low control; limited stack changes',
    ds: 'Complete control at hardware + OS layer',
  },
  {
    label: 'Resources',
    sm: 'Allocated resources (CPU, RAM, NVMe) you can scale',
    mv: 'Allocated VPS resources',
    sh: 'Pooled resources; no guaranteed vCPU/RAM',
    ds: 'Own dedicated resources at machine level',
  },
  {
    label: 'Management',
    sm: 'You are responsible for OS, Software and maintenance',
    mv: 'Updates and monitoring handled for you',
    sh: 'Managed by Hosting Services',
    ds: 'You/your team manage everything end-to-end',
  },
  {
    label: 'Best for',
    sm: 'Flexibility and control',
    mv: 'Teams wanting power with less upkeep',
    sh: 'Small to medium websites',
    ds: 'Compliance, extreme scale, custom networking',
  },
];

const VpsComparison = () => (
  <Section id="comparison" sx={{ background: C.bgSection, borderBottom: `1px solid ${C.border}` }}>
    <Container maxWidth="lg">

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 5 }}>
        <SectionLabel>Plan Comparison</SectionLabel>
        <Typography variant="h2" sx={{
          fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.4rem' },
          color: C.textPrimary, letterSpacing: '-0.03em', mb: 1.5,
        }}>
          Is Self-Managed VPS hosting right for you?
        </Typography>
        <Typography sx={{ color: C.textSecondary, fontSize: '1rem', maxWidth: 580, mx: 'auto', lineHeight: 1.6 }}>
          See how VPS hosting differs from managed VPS, shared hosting, and a dedicated server.
          Pick the hosting plan that fits your goals, budget, and requirements.
        </Typography>
      </Box>

      {/* Comparison Table */}
      <TableContainer component={Paper} elevation={0} sx={{
        border: `1px solid ${C.border}`,
        borderRadius: '16px',
        overflow: 'hidden',
        background: '#FFFFFF',
      }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: '#F1F5F9' }}>
              <TableCell sx={{
                fontWeight: 700, fontSize: '0.82rem', color: C.textMuted,
                borderBottom: `1px solid ${C.border}`, width: '18%', py: 2.5, px: 3,
              }}>
                Feature
              </TableCell>
              {cols.map((col) => (
                <TableCell key={col.key} align="center" sx={{
                  borderBottom: `1px solid ${C.border}`, py: 2.5, px: 2,
                  background: col.highlight ? C.primaryLight : 'transparent',
                }}>
                  <Typography sx={{
                    fontWeight: col.highlight ? 800 : 700,
                    fontSize: '0.9rem',
                    color: col.highlight ? C.primary : C.textPrimary,
                  }}>
                    {col.highlight && '⚡ '}{col.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={row.label} sx={{
                background: i % 2 === 0 ? '#FFFFFF' : '#FAFAFA',
                '&:last-child td': { borderBottom: 0 },
              }}>
                <TableCell sx={{
                  fontWeight: 700, fontSize: '0.85rem', color: C.textPrimary,
                  borderBottom: `1px solid ${C.border}`, py: 2, px: 3,
                }}>
                  {row.label}
                </TableCell>
                {cols.map((col) => (
                  <TableCell key={col.key} align="center" sx={{
                    fontSize: '0.84rem',
                    color: col.highlight ? C.textPrimary : C.textSecondary,
                    fontWeight: col.highlight ? 600 : 400,
                    borderBottom: `1px solid ${C.border}`,
                    background: col.highlight ? `${C.primaryLight}50` : 'transparent',
                    py: 2, px: 2,
                  }}>
                    {col.highlight && (
                      <CheckCircle sx={{ fontSize: 15, color: C.success, mr: 0.75, verticalAlign: 'middle' }} />
                    )}
                    {row[col.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

    </Container>
  </Section>
);

export default VpsComparison;
